import React from "react";
import { Shield, AlertCircle, Phone, Mail } from "lucide-react";

/**
 * Safeguarding Policy — covers children, vulnerable adults and online conduct.
 * Required document for UK CIO application and good-practice for any
 * community organisation working with diverse age groups online and in person.
 */

const PRINCIPLES = [
  "The welfare of every child, young person and vulnerable adult is paramount in all ABGN-UK activities.",
  "Everyone, regardless of age, gender, ethnicity, disability, religion or sexual orientation, has the right to participate safely in our online and in-person activities.",
  "All concerns about a child's or adult's welfare will be taken seriously and responded to without delay.",
  "Volunteers and members will be informed of, and trained in, this policy as relevant to their role.",
];

const PROCEDURES = [
  {
    title: "Charitable digital platform (online sessions, livestreams, forums, chat)",
    items: [
      "The ABGN-UK platform is charitable infrastructure run by the CIO. Every interactive space on the platform is moderated and bounded by this policy.",
      "All meeting rooms require either an ABGN-UK login or a private invite code shared by the host. No room is publicly indexed.",
      "Meeting hosts are designated and are the only participants with moderation controls (mute, remove, end).",
      "Children under 16 may attend ABGN-UK online events only when accompanied by a parent or guardian.",
      "Community discussion forums are moderated. Posts in breach of this policy or our Equality & Diversity statement are removed; persistent breach results in suspension.",
      "Direct messaging between adult volunteers and minors is not permitted on the platform. Communication with minors is conducted in group settings or via their parent / guardian.",
      "Youth learning programmes delivered on the platform run with a trained moderator present at all times; sessions are logged but never broadcast outside the registered cohort.",
    ],
  },
  {
    title: "In-person events",
    items: [
      "Two adults (DBS-checked where required) are present at any event involving children.",
      "Photo / video consent is requested in writing before any image of a minor is published.",
      "Risk assessments are carried out for every public-facing event.",
      "First-aid trained volunteers are present at all events of 25+ attendees.",
    ],
  },
  {
    title: "Reporting a concern",
    items: [
      "Any safeguarding concern should be reported immediately to the Designated Safeguarding Lead at info@ambedkaritebuddhistglobalnetworkuk.com with the subject line \"Safeguarding\".",
      "If a child or adult is in immediate danger, contact the police on 999. For non-emergency police, call 101.",
      "For child-safeguarding advice in the UK: NSPCC Helpline 0808 800 5000 (24/7).",
      "Reports are treated confidentially and shared only on a need-to-know basis with statutory authorities where required.",
    ],
  },
  {
    title: "Volunteer expectations",
    items: [
      "All volunteers read this policy on registration and re-read annually.",
      "Volunteers working regularly with children or vulnerable adults complete an enhanced DBS check at ABGN-UK's expense.",
      "Volunteers do not transport children alone, do not arrange to meet minors privately, and do not use their personal phone numbers for routine contact with minors.",
      "Volunteers are encouraged to challenge or report any behaviour that conflicts with this policy. \"Whistleblowing\" in good faith is protected.",
    ],
  },
];

export default function Safeguarding() {
  return (
    <div className="bg-pearl">
      <section className="bg-navy text-pearl">
        <div className="section-container py-16">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-saffron/15 border border-saffron/40 text-saffron mb-6">
            <Shield size={24} />
          </div>
          <p className="eyebrow text-saffron">Policy</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">Safeguarding policy</h1>
          <p className="mt-5 text-pearl/80 max-w-3xl leading-relaxed">
            ABGN-UK is committed to protecting children, young people and vulnerable adults
            participating in our activities, online and in person. This policy applies to
            every member, volunteer, host and visitor.
          </p>
          <p className="mt-3 text-xs text-pearl/55">
            Approved February 2026 · Designated Safeguarding Lead: ABGN-UK Volunteer Governance Committee · Reviewed annually.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-4xl">
          <h2 className="font-display text-3xl text-navy">Our principles</h2>
          <ul className="mt-6 space-y-3">
            {PRINCIPLES.map((p, i) => (
              <li key={i} className="bg-white border border-navy/10 rounded-sm p-5 text-slate-700 leading-relaxed">
                <span className="font-display text-saffron mr-2">{i + 1}.</span>
                {p}
              </li>
            ))}
          </ul>

          {PROCEDURES.map((sec) => (
            <div key={sec.title} className="mt-12">
              <h2 className="font-display text-2xl sm:text-3xl text-navy">{sec.title}</h2>
              <ul className="mt-5 space-y-3 text-slate-700">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 border-l-2 border-saffron/40 pl-4 py-1 leading-relaxed">
                    <span className="font-display text-saffron mt-0.5">•</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="mt-16 bg-amber-50 border border-amber-300 rounded-sm p-6 flex items-start gap-4">
            <AlertCircle size={22} className="text-amber-700 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-display text-xl text-navy">In an emergency</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                If you believe a child or adult is in immediate danger, call <strong>999</strong> (UK) before contacting ABGN-UK.
                For non-emergency safeguarding advice, the NSPCC Helpline operates 24/7 on <strong>0808 800 5000</strong>.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <a href="tel:999" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-sm">
                  <Phone size={12} /> 999 emergency
                </a>
                <a href="mailto:info@ambedkaritebuddhistglobalnetworkuk.com?subject=Safeguarding" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-navy text-pearl rounded-sm" data-testid="safeguarding-email">
                  <Mail size={12} /> Report to ABGN-UK
                </a>
              </div>
            </div>
          </div>

          <p className="mt-12 text-xs text-slate-500 italic">
            This policy will be reviewed annually by the volunteer governance committee, or sooner if guidance from the Charity Commission, Office of the Regulator of Community Interest Companies, or relevant statutory body materially changes.
          </p>
        </div>
      </section>
    </div>
  );
}
